import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-daily-goal-date-to-achievements',
  templateUrl: './daily-goal-date-to-achievements.component.html',
  styleUrls: ['./daily-goal-date-to-achievements.component.sass']
})
export class DailyGoalDateToAchievementsComponent implements OnChanges {
  @Input() rows: any[] = [];
  @Input() field: any;
  @Output() onChange = new EventEmitter<any[]>();
  @Input() readonly: boolean = false;

  newDate: Date | null = null;
  newAchievements: any[] = [];
  searchResults: any[] = [];
  errorMessage: string | null = null;

  // Resolved achievement titles keyed by numeric id (rows only store the ids).
  titlesById: { [id: number]: string } = {};

  constructor(private dataService: DataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rows']) {
      this.loadTitles();
    }
  }

  get sortedRows(): any[] {
    return [...(this.rows || [])].sort(
      (a, b) => new Date(a?.date ?? 0).getTime() - new Date(b?.date ?? 0).getTime(),
    );
  }

  get hasPendingSelection(): boolean {
    return !!this.newDate || this.newAchievements?.length > 0;
  }

  getTime(date: any): number {
    return date ? new Date(date).getTime() : 0;
  }

  getTitle(id: number): string {
    return this.titlesById[id] || '';
  }

  async loadTitles() {
    const ids = Array.from(
      new Set(
        (this.rows || [])
          .flatMap((r: any) => r?.achievementIds || [])
          .filter((id: any) => id !== null && id !== undefined && this.titlesById[id] === undefined),
      ),
    );
    if (ids.length === 0) return;
    try {
      const result = await this.dataService.getAllOfTypeAsync(
        'achievements',
        {
          query: { id: { $in: ids } },
          select: '_id id title',
        },
      );
      for (const achievement of result || []) {
        this.titlesById[achievement.id] = achievement.title;
      }
    } catch {
      // Leave titles unresolved; the id links are still shown.
    }
  }

  async searchAchievements(event: any) {
    const query = event.query ?? '';
    try {
      const numericId = query && !isNaN(Number(query)) ? Number(query) : null;
      const searchQuery = query
        ? { $or: [{ title: { $regex: query, $options: 'i' } }, ...(numericId !== null ? [{ id: numericId }] : [])] }
        : {};
      const result = await this.dataService.getAllOfTypeAsync(
        'achievements',
        {
          query: searchQuery,
          select: '_id id title',
          sort: { title: 1 },
        }
      );
      this.searchResults = result || [];
    } catch {
      this.searchResults = [];
    }
  }

  addRow() {
    this.errorMessage = null;

    if (!this.newDate) {
      this.errorMessage = 'Select a date/time.';
      return;
    }
    const achievements = (this.newAchievements || []).filter(
      (a: any) => a?.id !== null && a?.id !== undefined,
    );
    if (achievements.length === 0) {
      this.errorMessage = 'Select at least one achievement.';
      return;
    }
    if ((this.rows || []).some((r: any) => this.getTime(r.date) === this.getTime(this.newDate))) {
      this.errorMessage = 'That date/time is already assigned achievements.';
      return;
    }

    for (const achievement of achievements) {
      this.titlesById[achievement.id] = achievement.title;
    }
    const updated = [
      ...(this.rows || []),
      { date: this.newDate, achievementIds: achievements.map((a: any) => a.id) },
    ];
    this.onChange.emit(updated);
    this.newDate = null;
    this.newAchievements = [];
    this.searchResults = [];
  }

  removeRow(date: any) {
    const target = this.getTime(date);
    const updated = (this.rows || []).filter((r: any) => this.getTime(r.date) !== target);
    this.onChange.emit(updated);
  }
}
